/** Decides what the driver sees and hears. Pure logic, no network, no DOM —
 *  so the whole alerting behaviour is testable against a replayed GPS trace. */

import { isAhead, timeToHazard, type Point } from "./geo.js";
import { HAZARDS, type HazardType } from "./hazards.js";
import { decayState } from "./decay.js";

export type Alert = {
  id: string;
  type: HazardType;
  lat: number;
  lon: number;
  radiusM: number;
  reporterCourse: number | null;
  samples: number;
  confidence: number;
  source: "user" | "official" | "osm" | "bridge";
  provenance: string | null;
  sourceText: string | null;
  createdAt: number;
  expiresAt: number;
  /** Hold band: not yet confirmed by a second driver. Drawn translucent with a
   *  Saktë?/Gabim prompt, never spoken. */
  hold?: boolean;
};

export type Fix = {
  lat: number;
  lon: number;
  /** GPS course over ground. Never the compass. */
  course: number | null;
  speedMps: number;
  at: number;
};

/** Primary announcement 10 s out, secondary 3 s out. Distance is derived from
 *  speed, not fixed: 10 s is 306 m at 110 km/h but only 55 m at 20 km/h. */
export const TTH_PRIMARY_S = 10;
export const TTH_SECONDARY_S = 3;
const PRIMARY_CLAMP_M = { min: 150, max: 500 };

export type Announcement = { alert: Alert; stage: "primary" | "secondary"; distance: number };

export type Visible = { alert: Alert; distance: number; tth: number };

/** While navigating, "ahead" is measured along the route instead of by bearing.
 *  Returns the route distance to the hazard, or null when it is not on the route
 *  ahead. Installed by the navigator, removed when the driver leaves the route. */
export type AheadFn = (alert: Alert, fix: Fix) => number | null;

export class AlertEngine {
  private alerts = new Map<string, Alert>();
  private ahead: AheadFn | null = null;

  setAhead(fn: AheadFn | null): void {
    this.ahead = fn;
  }
  /** Which stages have already been spoken for a given report, this drive. */
  private announced = new Map<string, Set<"primary" | "secondary">>();

  replaceCell(_cell: string, items: readonly Alert[]): void {
    for (const a of items) this.alerts.set(a.id, a);
  }

  upsert(alert: Alert): void {
    this.alerts.set(alert.id, alert);
  }

  remove(id: string): void {
    this.alerts.delete(id);
    this.announced.delete(id);
  }

  get size(): number {
    return this.alerts.size;
  }

  /** Everything held, unfiltered. Harta shows all of it; drive mode never does. */
  all(): Alert[] {
    return [...this.alerts.values()];
  }

  /** Drop anything past its fade-out so the map cannot accumulate ghosts. */
  prune(now: number): number {
    let dropped = 0;
    for (const [id, a] of this.alerts) {
      if (decayState(a.type, a.createdAt, a.expiresAt, now).expired) {
        this.alerts.delete(id);
        this.announced.delete(id);
        dropped++;
      }
    }
    return dropped;
  }

  /** Everything ahead of the driver, nearest first. */
  visible(fix: Fix, limit = 6): Visible[] {
    const me: Point = { lat: fix.lat, lon: fix.lon };
    const out: Visible[] = [];

    for (const alert of this.alerts.values()) {
      if (fix.at >= alert.expiresAt + 60_000) continue;
      const spec = HAZARDS[alert.type];
      if (this.ahead !== null) {
        const d = this.ahead(alert, fix);
        if (d !== null && d <= Math.max(3000, fix.speedMps * 45)) {
          out.push({ alert, distance: d, tth: timeToHazard(d, fix.speedMps) });
        }
        continue;
      }
      const verdict = isAhead({
        me,
        course: fix.course,
        speedMps: fix.speedMps,
        hazard: { lat: alert.lat, lon: alert.lon },
        directional: spec.directional,
        reporterCourse: alert.reporterCourse,
      });
      if (!verdict.show) continue;
      out.push({ alert, distance: verdict.distance, tth: timeToHazard(verdict.distance, fix.speedMps) });
    }

    out.sort((a, b) => a.distance - b.distance);
    // Above 20 km/h the map is capped hard: fewer things to look at is the whole point.
    return out.slice(0, fix.speedMps > 5.6 ? limit : Math.max(limit, 12));
  }

  /**
   * Which alerts should speak right now. Each report speaks at most once per stage
   * per drive; "info" severity is shown but never spoken.
   */
  due(fix: Fix): Announcement[] {
    const out: Announcement[] = [];
    for (const v of this.visible(fix, 12)) {
      const spec = HAZARDS[v.alert.type];
      if (spec.severity === "info") continue;
      // An unconfirmed report is shown, not announced: one driver's tap is not
      // enough to make the car speak.
      if (v.alert.hold === true) continue;

      const spoken = this.announced.get(v.alert.id) ?? new Set<"primary" | "secondary">();
      const primaryDistance = Math.min(
        PRIMARY_CLAMP_M.max,
        Math.max(PRIMARY_CLAMP_M.min, fix.speedMps * TTH_PRIMARY_S),
      );

      if (!spoken.has("primary") && v.distance <= primaryDistance) {
        spoken.add("primary");
        out.push({ alert: v.alert, stage: "primary", distance: v.distance });
      } else if (
        spoken.has("primary") && !spoken.has("secondary") &&
        v.tth <= TTH_SECONDARY_S && v.distance > 40
      ) {
        spoken.add("secondary");
        out.push({ alert: v.alert, stage: "secondary", distance: v.distance });
      }
      this.announced.set(v.alert.id, spoken);
    }
    return out;
  }

  /** How many hazards have spoken this drive: the product's one metric. */
  get announcedCount(): number {
    return this.announced.size;
  }

  /** Start of a new drive: everything may be announced again. */
  resetAnnouncements(): void {
    this.announced.clear();
  }
}

/** Static clip path for a hazard + stage. 12 types x 2 stages, shipped as files. */
/** Bump when clips are regenerated. Also the cache-buster after the incident
 *  where the platform served the HTML page for clips that did not exist yet and
 *  browsers cached that HTML under the clip URL for a week. */
export const VOICE_VERSION = "2";

export function clipUrl(type: HazardType, stage: "primary" | "secondary"): string {
  return `/audio/${type}-${stage}.mp3?v=${VOICE_VERSION}`;
}

export function navClipUrl(fragment: string): string {
  return `/audio/nav/${fragment}.mp3?v=${VOICE_VERSION}`;
}
